import { FreedataValueVO } from '../../vo/core/freedatavalue.vo';
import { ConnectVO } from '../../vo/core/connect.vo';
import { Model } from '../../model/model';
import { cloneDeep } from 'lodash-es';
import { AiResponse, Attachment, Baseline, BaselineValue, BusinessException, ConnectAction, EntityModificationPack, VisionStep } from '@wizeoo/akm-message-lib';
import { IFreedataWidget, IWidget } from '../widgets/i-widget';
import { ConfirmationResultObj, WizCommandManager, WizEventManager, WizIcon, WizPopUp, WizRoot } from '@wizeoo/akm-component-lib';
import { CONFIRMATION_MODE, CONFIRMATION_RESULT, WIZ_COMMAND_STATE } from '@wizeoo/akm-core-lib';
import { ALK_EVENT_TYPE, CONNECT_RETURN_ACTION, PROCESS_MODE, WIDGET_GLOBAL_KIND } from '../../model/enums';
import { BaselineEngine, COMPUTE_MODE, EM_ACTION, VisionVO, WidgetLine, WizConstants } from '@wizeoo/akm-compute-lib';
import { ConnectCmd } from '../../command/batch/connect.cmd';
import { ConnectSmallRenderer } from './connectrenderers/connect-small.renderer';
import { VisionManageLinesCmd } from '../../command/vision/vision-managelines.cmd';
import { ConnectReturnRenderer } from './connectrenderers/connect-return-renderer';
import { AlkEvent } from '../../framework/events/alk.event';
import { FeiVO } from '../../vo/workingdata/fei.vo';
import { BaselinevalueBulkerCmd } from '../../command/batch/baselinevalue.bulker.cmd';
import { EMEngine } from '../entities/entity-modification.engine';
import { CommitCmd } from '../../command/em/commit.cmd';
import { ViewData } from '../../model/view.data';
import { BusinessUnitVO } from '../../vo/core/business-unit.vo';
import { DomainVO } from '../../vo/core/domain.vo';
import { getDefaultWidgetConfigurationObj } from './../../manager/widgets/widget.engine';
import { FreedataArgsVO } from '../../vo/core/freedataargs.vo';
import { AttachmentService } from '../../command/core/attachment/attachment.service';
import { AiCmd } from '../../command/external/ai.cmd';

// params d'un objet connect
export interface ConnectParameter {
  range?: string; // portée, LOCAL ou REMOTE
  process?: string; // nom du process pour les fonctions locales
  step?: number; // step cible éventuel
  pairs?: BulkPair[]; // paires de mesures à cloner
  next?: string; // code de la fonction connect à jouer à la suite
  usePool?: boolean; // utilisation du pool de données du widget (prise en compte des filtres)
  useFei?: boolean; // utilisation d'un niveau complet de step
  fdvs?: FreedataValueVO[]; // le FDV / la sélection de FDVs
  baselines?: Baseline[]; // la baseline / la sélection de BLs
  objetKind?: string; // le type d'objet porté (selon le widget source)
}

// demande de clonage de mesures calculées en valeurs persistées (S)
export interface BulkRequest {
  step: number; // fei cible pour le clonage de baselines
  pairs: BulkPair[]; // les paires à cloner : [mesure source, mesure cible]
}

// une paire de mesures
export interface BulkPair {
  to: string; // mesure source
  from: string; // mesure cible
}

// mise en attente des paramètres sur traitement asynchrone (cloneMeasure)
export interface ConnectPendingParams {
  cnx: ConnectVO; // fonction connect en attente de traitement
  callback: Function; // callback à appeler sur fin de traitement asynchrone
}

/**
 * Manager des connexions
 */
export class ConnectEngine {

  public static LOCAL_PROCESS: string = 'LOCAL'; // fonction locale
  public static LOCAL_CLONE_MEASURE: string = 'CLONE_MEASURE'; // fonction locale: clonage de mesures
  // {
  //   "process":"CLONE_MEASURE" ==> nom du process local
  //   "next":"DUPIMP", ==> fonction connect à chainer
  //   "step":4, ==> step cible
  //   "pairs":[{"to":"MES_BESOINCOMMANDEEAN#S","from":"MES_BESOINCOMMANDEEAN#I"}], ==> paires de mesures à cloner. From cloné sur To
  //   "useFei":"true", ==> 
  //   "usePool": "false" ==> utilisation du pool de données du widget (prise en compte des filtres)
  // }

  public static LOCAL_ADD_BASELINES: string = 'ADD_BASELINES'; // fonction locale: ajout des nouvelles baselines et de leurs enfants
  // {
  //   "process":"ADD_BASELINES" ==> nom du process local
  //   "step":3, ==> step max cible. On ne génère PAS en DESSOUS de ce step (il est donc généré)
  // }


  protected _model: Model = null;
  constructor(model: Model) {
    this._model = model;
  }
  public get model(): Model {
    return this._model as Model;
  }

  /**
   * récupération de la liste de toutes les fonctions connect
   * depuis la map
   * @returns ConnectVO[]
   */
  public get connectList(): ConnectVO[] {
    let result: ConnectVO[] = [];
    [...this._model.connectMapExt?.values()].forEach(byBu => {
      [...byBu.values()].forEach(byCode => {
        [...byCode.values()].forEach((cnx: ConnectVO) => {
          result.push(cnx);
        });
      });
    });

    return result;
  }

  /**
   * sur changement des Connect, alimentation du modèle
   * @param connect: ConnectAction[]
   */
  public buildMap(connect: ConnectAction[]): void {
    const cnx: ConnectVO[] = connect.map((con) => new ConnectVO(this.model, con));

    if (!this._model.connectMapExt) {
      this._model.connectMapExt = new Map();
    }

    // construction de la map / BU
    const map = this._model.connectMapExt;
    cnx.forEach((r: ConnectVO) => {
      if (!map.has(r.domain)) {
        map.set(r.domain, new Map());
      }
      if (!map.get(r.domain).has(r.businessUnitCode)) {
        map.get(r.domain).set(r.businessUnitCode, new Map());
      }
      map.get(r.domain).get(r.businessUnitCode).set(r.code, r);
    });

  }

  /**
   * Clone 
   * @param con le cnx à cloner
   * @return ConnectVO,  Un clone
   */
  public clone(con: ConnectVO): ConnectVO {
    const clone = new ConnectVO(this.model, cloneDeep(con.source));
    return clone;
  }

  /** création d'un nouveau style */
  public buildFake(): ConnectVO {
    const result: ConnectVO = new ConnectVO(this.model);
    result.domain = this._model.domainCode;
    result.businessUnitCode = this._model.businessUnitCode;
    return result;
  }

  /**
   * Récupération d'une cnx par son code
   * @param code Le code
   */
  public getConnect(code: string, domain?: string, bu?: string): ConnectVO {
    let svo: ConnectVO = null;
    svo = this._model.connectMapExt?.get(domain || this.model.domainCode)?.get(bu || this._model.businessUnitCode).get(code);
    return svo;
  }

  /**
   * suppression de l'instance d'un cnx
   * @param code Le code du style
   */
  public removeConnect(code: string, domain?: string, bu?: string): ConnectVO {
    const toDie: ConnectVO = this.getConnect(code, domain, bu);
    this._model.connectMapExt?.get(domain || this.model.domainCode)?.get(bu || this._model.businessUnitCode).delete(code);
    return toDie;
  }

  /**
   * ajout de l'instance d'un ConnectVO
   * @param vo ConnectVO
   */
  public addConnect(vo: ConnectVO): ConnectVO {
    if (!this._model.connectMapExt.has(vo.domain)) {
      this._model.connectMapExt.set(vo.domain, new Map());
    }

    if (!this._model.connectMapExt.get(vo.domain)?.has(vo.businessUnitCode)) {
      this._model.connectMapExt.get(vo.domain)?.set(vo.businessUnitCode, new Map());
    }

    this._model.connectMapExt.get(vo.domain).get(vo.businessUnitCode).set(vo.code, vo);
    return vo;
  }


  /* ##############################################################################
            appel connect
  ############################################################################## */

  /**
   * appel d'une fonction connect
   * @param cnxStr code de la connexion (sur la BU)
   * @param widget source, peut être null en mode template (ne rien faire)
   * @param data cible réduite (ligne, pool)
   */
  public doArgActionExt(cnxStr: string, widget: IWidget, data?: any[]): void {

    // console.log('doArgActionExt', cnxStr, widget, data);
    // la fonction connect
    let cnx: ConnectVO = this.getConnect(cnxStr);

    if (!cnx) {
      WizPopUp.toast('$.connect.unknow.$', WizIcon.TOAST_ERROR).show();
      return;
    }
    const res: ConfirmationResultObj = new ConfirmationResultObj();
    res.result = CONFIRMATION_RESULT.CONFIRM;
    res.data = [cnx, widget, data];
    // this.doArgActionReallyParams = res;

    if (cnx.useWarning) {
      WizPopUp.confirm(
        cnx.trsWarnTitle,
        null,
        this.doArgActionReally.bind(this),
        CONFIRMATION_MODE.CONFIRM_CANCEL,
        [cnx, widget, data],
        ConnectSmallRenderer,
        null
      ).show();
    } else {
      this.doArgActionReally(res);
    }
  }

  // passage des params pour la pop de confirmation
  // private doArgActionReallyParams: ConfirmationResultObj = null;

  /** appel véritable */
  public doArgActionReally(result: ConfirmationResultObj): void {

    if (!!result && result.result !== CONFIRMATION_RESULT.CONFIRM) { // confirmation non...
      return;
    }

    // result = this.doArgActionReallyParams;
    // this.doArgActionReallyParams = null;

    WizCommandManager.setLock(true); // on pose le verrou
    const cnx: ConnectVO = result.data[0];
    const widget: IWidget = result.data[1];
    const data: any[] = result.data[2];

    if (!cnx.parameters) {
      cnx.parameters = {};
    }


    let usePool: boolean = cnx.parameters.usePool || false; // utilisation du pool complet du widget
    let version: number = cnx.parameters.version || 1; // version connect
    let keyMap: Map<string, string[]> = null;

    // console.log('doArgActionExt', cnx.parameters);

    if (widget?.widgetGlobalKind === WIDGET_GLOBAL_KIND.BASELINE) { // mode baseline
      cnx.parameters.objetKind = WIDGET_GLOBAL_KIND.BASELINE;

      let lines: Baseline[] = [];
      if (usePool) {
        // lines = widget.fei.getAllLines().map(wl => wl.bl);
      } else {
        if (data) {
          data.forEach((wl: WidgetLine) => {
            if (wl.ch?.size > 0) {
              wl.ch.forEach(subwl => {
                lines.push(subwl.bl);
              });
            } else {
              lines.push(wl.bl);
            }

          });
        }
      }
      cnx.parameters.baselines = lines;

    } else if (widget?.widgetGlobalKind === WIDGET_GLOBAL_KIND.FREEDATA) { // mode freedata
      cnx.parameters.objetKind = WIDGET_GLOBAL_KIND.FREEDATA;
      let lines: FreedataValueVO[] = [];
      if (usePool) { // TOUT le pool, pas de sélection sur ce FD
        // lines = (widget as IFreedataWidget).fdvDS.getWorkedSource();
      } else {
        if (data) {

          let fdKey: string = (widget as IFreedataWidget).freedataCode;
          keyMap = new Map();
          keyMap.set(fdKey, []);

          data.forEach((fdv: FreedataValueVO) => {
            lines.push(fdv);
            keyMap.get(fdKey).push(fdv.shortCode);
          });
        }
      }
      // TODO obsolète, faire plus simple
      cnx.parameters.fdvs = lines;
      // construction de la clé de filtre par FD

    } else { // mode libre, pas de données
      cnx.parameters.objetKind = WIDGET_GLOBAL_KIND.NONE;
    }


    /**
     * gestion des appels 'Cnx V2'
     */
    if (version === 3) {
      // console.log('doArgActionExt2 version', cnx.parameters);

      // construction du wrapper(body)
      let cmd: ConnectCmd = new ConnectCmd(this._model);
      let wrapper: any = cmd.wrapper(); // va alimenter dom, bu, vision...
      wrapper.code = cnx.processCode;
      wrapper.mode = cnx.processMode;
      wrapper.connectFunctionCode = cnx.code;

      if (!!cnx.parameters.engines) {
        wrapper.engines = cnx.parameters.engines;
      } else {
        wrapper.engines = {};
      }

      //TODO TODO injection des clés de baselines
      if (cnx.parameters?.baselines) {
        let keys: string[] = [];
        cnx.parameters.baselines.forEach(bl => {
          keys.push(bl.key);
        });
        let blkeys: string = keys.join(',');
        wrapper.engines.mode = 'BASELINE';
        wrapper.engines.keys = blkeys;
      }


      // On prend les informations venant des paramètres, hors prédéfinies, pour en faire des paramètres applicatifs
      try {
        const predefined: string[] = ["usePool", "version", "objetKind", "baselines", "fdvs"];
        let params = JSON.parse(cnx.trsParameters);
        for (const [key, val] of Object.entries(params)) {
          if (!predefined.includes(key)) {
            wrapper.engines[key] = val;
          }
        }
      } catch (e: any) {
        console.log('oups', e);
      }

      // alimentation des clés
      if (!!keyMap) {
        [...keyMap.keys()].forEach(fdkey => {
          wrapper.engines[fdkey] = keyMap.get(fdkey).join(WizConstants.FILTER_ELEMENT_SEPARATOR);
        });
      }

      // console.log('doArgActionExt2 body', body);
      new ConnectCmd(this._model).call(cnx, wrapper, this.backFromConnect.bind(this)).execute();

    }
    else if (version === 2) {
      // console.log('doArgActionExt2 version', cnx.parameters);

      // construction du wrapper(body)
      let cmd: ConnectCmd = new ConnectCmd(this._model);
      let wrapper: any = cmd.wrapper(); // va alimenter dom, bu, vision...
      wrapper.code = cnx.processCode;
      wrapper.mode = cnx.processMode;
      wrapper.connectFunctionCode = cnx.code;

      if (cnx.parameters.objetKind === WIDGET_GLOBAL_KIND.FREEDATA || cnx.parameters.objetKind === WIDGET_GLOBAL_KIND.NONE) { // si appel depuis les Freedatas, enregistrement dans __SETTINGS de la BU
        wrapper.visionCode = WizConstants.wildcardDomainForConnect;
      }

      if (!!cnx.parameters.engines) {
        wrapper.engines = cnx.parameters.engines;
      } else {
        wrapper.engines = {};
      }

      // alimentation des clés
      if (!!keyMap) {
        [...keyMap.keys()].forEach(fdkey => {
          wrapper.engines[fdkey] = keyMap.get(fdkey).join(WizConstants.FILTER_ELEMENT_SEPARATOR);
        });
      }

      // console.log('doArgActionExt2 body', body);
      new ConnectCmd(this._model).call(cnx, wrapper, this.backFromConnect.bind(this)).execute();

    }
    else if (cnx.processCode === ConnectEngine.LOCAL_PROCESS) {
      switch (cnx.parameters.process) {
        case ConnectEngine.LOCAL_CLONE_MEASURE:
          // const blvs: BaselineValue[] = 
          this.clonePack(cnx, this.bulkBaselines);
          break;
        case ConnectEngine.LOCAL_ADD_BASELINES:
          // construction de la pile de steps à générer
          let maxStep: number = cnx.parameters?.step || Object.values(this._model.currentVision.visionSteps).length + 1;
          let stepArr: number[] = [];
          let idx: number = 1;
          while (idx <= maxStep) {
            stepArr.push(idx);
            idx++;
          }

          new VisionManageLinesCmd(this._model)
            .generateNewLines(this._model.currentVision, stepArr, this.backFromConnect.bind(this), cnx)
            .execute();
          break;
      }
    }
    else {
      if (cnx.parameters.objetKind === WIDGET_GLOBAL_KIND.BASELINE) {
        const lines: Baseline[] = cnx.parameters.baselines;
        new ConnectCmd(this._model).callFromBaseline(cnx, lines, this.backFromConnect.bind(this)).execute();
      } else if (cnx.parameters.objetKind === WIDGET_GLOBAL_KIND.FREEDATA) {
        const fdv: FreedataValueVO = cnx.parameters.fdvs[0];
        new ConnectCmd(this._model).callFromFreedata(cnx, fdv?.getParentFDVO(), fdv, this.backFromConnect.bind(this)).execute();
      } else { // ni FDV ni Baseline, fonction libre
        new ConnectCmd(this._model).callFromFree(cnx, this.backFromConnect.bind(this)).execute();
      }
    }

  }

  /** 
   * retour de fonction connect
   * @param cmd: la commande
   */
  public backFromConnect(cmd: any): void {

    let cnx: ConnectVO = null;
    if (cmd.state === WIZ_COMMAND_STATE.FAULT || // erreur de service
      !cmd.cnx ||// pas de fonction connect...
      cmd.result?.status === 'ERROR' // erreur normalisée connect
    ) {
      cnx = cmd.cnx;

      // TODO tester aussi le result normalisé connect, pour lister les erreurs
      // TODO sur les fonctions natives (locales)

      // erreur de service, brute
      if (cmd.state === WIZ_COMMAND_STATE.FAULT) {
        // WizPopUp.toast(cmd.error.error, WizIcon.TOAST_ERROR).show();
      } else if (cnx?.trsResultError) {
        if (cmd.result?.error && cmd.result?.error.message) {
          WizPopUp.toast(cnx.trsResultError + ' : ' + cmd.result?.error.message, WizIcon.TOAST_ERROR).show();
        }
        else {
          WizPopUp.toast(cnx.trsResultError, WizIcon.TOAST_ERROR).show();
        }
      }

    } else {
      // console.log('cmd backFromConnect', cmd.cnx);
      cnx = cmd.cnx;
      if (!!cnx.parameters?.next) { // chainage
        // console.log('next', cnx.parameters.next);
        const next: ConnectVO = this._model.connectMapExt?.get(this.model.domainCode).get(this._model.businessUnitCode)?.get(cnx.parameters.next);

        // on lui passe les paramètres source de sélection
        if (!next.parameters) {
          next.parameters = {};
        }
        next.parameters.baselines = cnx.parameters.baselines;
        next.parameters.fdvs = cnx.parameters.fdvs;


        const res: ConfirmationResultObj = new ConfirmationResultObj();
        res.result = CONFIRMATION_RESULT.CONFIRM;
        res.data = [next, null, null];
        this.doArgActionReally.bind(this, res);
      }
      else { // pas de chainage

        // En synchrone, on pop ou toast selon l'action
        if (cnx.processMode === PROCESS_MODE.SYNC) {
          if (cnx.resultAction === CONNECT_RETURN_ACTION.POP) {
            let dataMessagePop: WizPopUp;
            const targetPosition: any = { right: '0px', bottom: '50px' }; // position physique
            dataMessagePop = new WizPopUp( // la taille sera donnée par le style (calc)
              ConnectReturnRenderer,
              true,
              false,
              null,
              '700px', // 350px',
              '350px', // calc(100% - 81px)',
              null, // params
              null,
              targetPosition
            );

            dataMessagePop.datas = cmd;
            dataMessagePop.show();

          } else if (cnx.resultAction === CONNECT_RETURN_ACTION.REFRESH) {
            WizPopUp.toast(cnx.trsResultSuccess || '$.call.success.$', WizIcon.TOAST_SUCCESS).show();
            setTimeout(() => {
              this._model.compositeEngine.compositeReload();
            }, (1000));
          } else { // juste l'info de retour de lancement
            WizPopUp.toast(cnx.trsResultSuccess || '$.call.success.$', WizIcon.TOAST_SUCCESS).show();
          }
        } else {
          // En asynchrone, pop ou toast sont déléguées au message socket, donc on toast juste l'info de retour de lancement
          WizPopUp.toast('$.connect.processing.$', WizIcon.TOAST_SUCCESS).show();
        }

      }
    }
    WizCommandManager.setLock(false); // on pose le verrou
  }


  public pendingParams: ConnectPendingParams = null; // params en attente

  /**
   * demande de clonage des lignes
   * @param cnx: ConnectVO la demande
   * @param callback : fonction de retour. Possiblement asynchrone, si le fei n'est pas encore chargé
   * @returns la collection de baselines à persister (bulk)
   */
  public clonePack(cnx: ConnectVO, callback: Function): void {

    // console.log('engine clonePack', cnx);

    let bulkRequest: ConnectParameter = cnx.parameters;
    this.pendingParams = null; // reset des params en attente

    let step: number = bulkRequest.step;
    if (!step) {
      step = bulkRequest.baselines[0]?.level;
    }
    if (!step) {
      return null;
    }
    this.pendingParams = { cnx, callback };

    const v: VisionVO = this._model.currentVision;
    const vs: VisionStep = BaselineEngine.getVisionStepByLevel(v, step)
    let feiProto: FeiVO = this._model.dataEngine.getFei(COMPUTE_MODE.LOCAL, step, WizConstants.BASE_SCENARIO) as FeiVO; // on le cherche
    // console.log('engine feiProto', feiProto);
    if (!feiProto) { // pas chargé, appel asynchrone
      feiProto = this._model.dataEngine.generateFei(v, vs, WizConstants.BASE_SCENARIO, getDefaultWidgetConfigurationObj()); // et on le crée s'il n'existe pas
      WizEventManager.addListener(WizRoot.root(), WizRoot.root(), ALK_EVENT_TYPE.FEI_COMPUTED, this.backFromAsyncClone.bind(this));
    } else {
      this.endClone(cnx, feiProto, callback);
    }
  }

  /**
   * 
   * @param e ALK_EVENT_TYPE.FEI_COMPUTED, data = feiVO
   */
  public backFromAsyncClone(e: AlkEvent): void {
    WizEventManager.removeListener(WizRoot.root(), WizRoot.root(), ALK_EVENT_TYPE.FEI_COMPUTED);
    this.endClone(this.pendingParams.cnx, e.data, this.pendingParams.callback);
    // console.log('back from async', e);
  }

  /**
   * clonage, tout le monde est dispo
   * @param cnx fonction connect
   * @param fei cible
   * @param callback 
   */
  public endClone(cnx: ConnectVO, fei: FeiVO, callback: Function): void {
    this.pendingParams = null; // reset sur les params
    if (!fei) {
      callback(cnx, null);
    }

    // console.log('BulkRequest', bulkRequest);
    let result: BaselineValue[] = [];
    let newBLV: BaselineValue;

    let bulkRequest: ConnectParameter = cnx.parameters;

    let pack: WidgetLine[] = []; // le pool cible pour le clonage
    // gestion du mode sélectif ?
    if (bulkRequest.useFei) { // mode useFei  
      pack = fei.getAllLines();
    }

    bulkRequest.pairs.forEach((bp: BulkPair) => {
      pack.forEach((wl: WidgetLine) => {
        newBLV = wl.bl.values[bp.to];
        if (!newBLV) {
          newBLV = BaselineEngine.generateNewBaselineValue(wl.bl, bp.to);
        }
        newBLV.valor = fei.model.cellMatrixEngine.getCellValue(wl.level, wl.key, wl.scen, bp.from);

        result.push(newBLV);
      });
    });

    // console.log('back from endClone', result);
    callback(cnx, result);
  }

  /** 
   * persistence des clones de mesures
   * @param cnx 
   * @param blvs 
   */
  public bulkBaselines(cnx: ConnectVO, blvs: BaselineValue[]): void {
    // console.log('ensure is loaded', cnx, blvs);
    if (blvs?.length > 0) {
      new BaselinevalueBulkerCmd(this._model).bulk(blvs, this.backFromConnect.bind(this), cnx).execute();
    } else { // pas la peine de jouer la requête, transmission directe pour la suite
      let cmd = new BaselinevalueBulkerCmd(this._model).bulk(blvs, null, cnx);
      cmd.state === WIZ_COMMAND_STATE.SUCCESS;
      this.backFromConnect(cmd);
    }
  }

  /**
   * create/ update d'un ConnectVO
   * @param to le clone en cours de modif
   * @param from l'objet d'origine, inaltéré
   * @param callback retour dans la vue
   */
  public merge(to: ConnectVO, from: ConnectVO, callback: Function): void {
    let pack: EntityModificationPack;
    if (to.exist) {
      pack = EMEngine.mergeEM(to, from);
    } else {
      pack = EMEngine.createEM(to);
    }

    if (pack && pack.entries.length > 0) {
      // a minima une modif, sinon, c'est le même objet (update)
      new CommitCmd(this._model).commit(pack, this.backFromCall.bind(this), [to], callback).execute();
    } else {
      // modèle, le clone est une copie de la valeur d'origine
      ViewData.wizUpdateModel = false;
      callback(); // retour en vue
    }
  }

  /**
   * delete d'un ConnectVO
   * @param to le clone à supprimer
   * @param callback retour dans la vue
   */
  public remove(to: ConnectVO, from: ConnectVO, callback: Function): void {
    const pack: EntityModificationPack = EMEngine.removeEM(to);
    new CommitCmd(this._model).commit(pack, this.backFromCall.bind(this), [to], callback).execute();
  }

  /**
   * gestion unifiée des retours de service CUD
   * @param cmd la commande commit qui porte les EMs et les objets
   */
  private backFromCall(cmd: CommitCmd): void {
    // modèle OK (rollback en cas d'erreur)
    ViewData.wizUpdateModel = false;

    // erreur physique
    if (cmd.state === WIZ_COMMAND_STATE.FAULT) {
      WizPopUp.toast('$.common.toast.service.error.$', WizIcon.TOAST_ERROR).show();
    } else {
      // le VO source
      const vo: ConnectVO = cmd.sourceVO[0] as ConnectVO;
      // une seule modification à la fois, packEntry donne l'objet
      const cnx: any = cmd.result.entries[0].entity;
      // l'action (un seul type)
      const operation: string = cmd.result.ems[0].operation;
      // la liste d'erreurs métier
      const errors: BusinessException[] = cmd.result.errors;
      // le statut du pack
      const packStatus: string = cmd.result.status;

      // le vrai VO (pas le clone)
      let realVO: ConnectVO = null;

      if (packStatus === WIZ_COMMAND_STATE.FAULT) {
        errors.forEach((be: BusinessException) => {
          WizPopUp.toast(be.message, WizIcon.TOAST_ERROR).show();
        });
      } else {
        switch (operation) {
          case EM_ACTION.UPDATE:
            vo.source = cnx; // le clone mis à jour
            realVO = this.getConnect(vo.code, vo.domain, vo.businessUnitCode);
            realVO.source = cnx;
            break;
          case EM_ACTION.CREATE:
            vo.source = cnx;
            realVO = this.addConnect(vo);
            break;
          case EM_ACTION.DELETE:
            vo.source = cnx;
            realVO = this.removeConnect(vo.code, vo.domain, vo.businessUnitCode);
            break;
        }
      }
      cmd.viewCallBack(realVO, operation); // retour en vue
    }
  }

  /** accesseur sur la BU */
  public getBusinessUnitVO(cnx: ConnectVO): BusinessUnitVO {
    return this.model.businessUnitEngine.getByBusinessUnitCode(cnx.domain, cnx.businessUnitCode);
  }

  /** accesseur sur la BU */
  public getDomainVO(cnx: ConnectVO): DomainVO {
    return this.model.businessUnitEngine.getDomainByCode(cnx.domain);
  }


  /* ##############################################################################
            appels IA
  ############################################################################## */

  /**
   * appel d'une fonction connect
   * @param cnxStr code de la connexion (sur la BU)
   * @param widget source, peut être null en mode template (ne rien faire)
   * @param data cible réduite (ligne, pool)
   */
  public doAiAction(cnxStr: string, widget: IWidget, arg: FreedataArgsVO, fdv: FreedataValueVO, callback: Function): void {
    
    let func: string = arg.settings.function;
    
    let from: string;
    let sourceValue: string;

    switch(func) {
      case 'imageToText':
        from = arg.settings.from;
        sourceValue = fdv.argsObjVO[from];
        if(!!sourceValue) {
          let att: string = AttachmentService.getInstance().getAttachment(sourceValue)[0];
          att = atob(att); 
          let q: string = "Décris ce que tu vois dans cette image (objets, contexte, couleurs). Fais une analyse technique de cette image (résolution, contraste, composition). Interprète sémantiquement cette image (émotions, symboles, ambiance). Retourne le résultat sous forme de JSON (ne l'encadre pas avec ```json)."
          let iacall: AiCmd = new AiCmd(this._model).imageToText(q, att, this.backFromIACall.bind(this));
          iacall.carrier = {widget, arg, fdv, callback};
          iacall.execute();
        } 

      break;
      case 'drawImage':
        from = arg.settings.from;
        sourceValue = fdv.argsObjVO[from];
        if(!!sourceValue) {
          let iacall: AiCmd = new AiCmd(this._model).drawImage('IMAGE', sourceValue, this.backFromIACall.bind(this));
          iacall.carrier = {widget, arg, fdv, callback};
          iacall.execute();
        } 
      break;
      case 'ocr':
        from = arg.settings.from;
        sourceValue = fdv.argsObjVO[from];
        if(!!sourceValue) {
          let att: string = AttachmentService.getInstance().getAttachment(sourceValue)[0];
          att = atob(att); 
          let q: string = "Effectue un traitement OCR sur cette image Retourne le résultat sous forme de JSON, une liste de clés-valeurs (ne l'encadre pas avec ```json)."
          let iacall: AiCmd = new AiCmd(this._model).imageToText(q, att, this.backFromIACall.bind(this));
          iacall.carrier = {widget, arg, fdv, callback};
          iacall.execute();
        } 
      break;
    } 
  }


  private backFromIACall(response: AiResponse, carrier: {widget: IWidget, arg: FreedataArgsVO, fdv: FreedataValueVO, callback: Function}): void {
    let to: string = carrier.arg.settings.to;
    let targetArg: FreedataArgsVO = carrier.fdv.getParentFDVO().getArgsVOMap().get(to);
    let fakeAE: any = { model:carrier.widget.model, fdv:carrier.fdv, arg:targetArg}; // un argEditorData, pas de lien direct pour pas de dépendance circulaire
    let func: string = carrier.arg.settings.function;
    
    let respValue: string = null;
    
    switch(func) {
      case 'imageToText':
        carrier.fdv.argsObjVO[to] = response.response;
        respValue = response.response;

      break;
      case 'drawImage':
        carrier.fdv.argsObjVO[to] = JSON.parse(response.response).blobId;
        respValue = JSON.parse(response.response).blobId;
      break;

    } 
    
    carrier.callback(fakeAE, respValue);
    console.log('backFromIACall', respValue);
    
    
    
    

  }



}
